import { createContext, ReactNode, useContext, useState } from 'react'
import type { Edge, Node, Viewport } from '@xyflow/react'
import type { EmittableTree } from '@/alerts/treeTypes'
import type { Calendar } from '@/alerts/calendar'
import type { KeptFromStored } from '@/utils/treeSerializer'

// The tree the editor opened, as the editor itself serializes it. Save compares against it to publish
// nothing when the author changed nothing, and writes `kept` back into the tree it saves.
export type LoadedTree = {
  kept: KeptFromStored
  baseline: EmittableTree | null
}

type FlowState = {
  initialNodes: Node[]
  initialEdges: Edge[]
  nodes: Node[]
  edges: Edge[]
  rootId: string
  viewport: Viewport
  // Node ids flagged by the Layer-A completeness check on a failed save, so the
  // canvas can highlight them. Empty when there is nothing to flag.
  invalidNodeIds: string[]
  // The calendar beside the root. Undefined when the tree has none.
  treeCalendar: Calendar | undefined
  // Hebrew reasons the tree calendar blocked the last save. Empty when it did not.
  treeCalendarIssues: string[]
  // Null for a new alert, and when the stored tree could not be opened.
  loaded: LoadedTree | null
  setLoaded: (l: LoadedTree | null) => void
  setNodes: (n: Node[]) => void
  setEdges: (e: Edge[]) => void
  setRootId: (id: string) => void
  setViewport: (v: Viewport) => void
  setInvalidNodeIds: (ids: string[]) => void
  setTreeCalendar: (c: Calendar | undefined) => void
  setTreeCalendarIssues: (issues: string[]) => void
}

const FlowStateContext = createContext<FlowState | null>(null)

export function FlowStateProvider({
  initialNodes,
  initialEdges,
  initialRootId,
  children,
}: {
  initialNodes: Node[]
  initialEdges: Edge[]
  initialRootId: string
  children: ReactNode
}) {
  const [nodes, setNodes] = useState<Node[]>(initialNodes)
  const [edges, setEdges] = useState<Edge[]>(initialEdges)
  const [rootId, setRootId] = useState<string>(initialRootId)
  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, zoom: 1 })
  const [invalidNodeIds, setInvalidNodeIds] = useState<string[]>([])
  const [loaded, setLoaded] = useState<LoadedTree | null>(null)
  const [treeCalendar, setTreeCalendar] = useState<Calendar | undefined>()
  const [treeCalendarIssues, setTreeCalendarIssues] = useState<string[]>([])

  return (
    <FlowStateContext.Provider
      value={{
        initialNodes,
        initialEdges,
        nodes,
        edges,
        rootId,
        viewport,
        invalidNodeIds,
        treeCalendar,
        treeCalendarIssues,
        loaded,
        setLoaded,
        setNodes,
        setEdges,
        setRootId,
        setViewport,
        setInvalidNodeIds,
        setTreeCalendar,
        setTreeCalendarIssues,
      }}
    >
      {children}
    </FlowStateContext.Provider>
  )
}

export function useFlowState(): FlowState {
  const ctx = useContext(FlowStateContext)
  if (!ctx) throw new Error('useFlowState must be inside FlowStateProvider')
  return ctx
}
